import { get, post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { downloadData } from "aws-amplify/storage";

// The web app pushes files into a dataset the way a client script does (stage 6 of
// docs/plans/DATASET_API_INGEST_PLAN.md): ask erodeapi for an upload link, POST the file to S3, then poll the
// upload's status while datasetIngestFunction validates it, keeps the original, writes the record and (for
// curated datasets) hands it to curation. The browser does no parsing of its own; ingest is the one validator.
// The reference client is scripts/datasetUploadTest/upload.js.

export type DatasetUploadStatus = "REQUESTED" | "RECEIVED" | "LOADED" | "REJECTED" | "FAILED" | "EXPIRED";

export interface DatasetUploadRow {
  id: string;
  datasetId: string;
  status: DatasetUploadStatus;
  source: "API" | "WEB" | "MODEL" | null;
  fileName: string | null;
  uploadedBy: string | null;
  uploadedByEmail: string | null;
  sourceTimezone: string | null;
  sizeBytes: number | null;
  rowCount: number | null;
  duplicateTimestamps: number | null;
  timeRange: { startDate?: string; endDate?: string } | null;
  columns: string[] | null;
  /** How the file was read: the timestamp header, and each stored column's header as sent */
  columnSources?: { timestamp?: string; columns?: Record<string, string> } | null;
  /** What the file declared about its columns (unit / name / description rows), by stored name */
  columnMetadata?: Record<string, { unit?: string; name?: string; description?: string }> | null;
  validationErrors: string[] | null;
  warnings: string[] | null;
  failureReason: string | null;
  recordId: string | null;
  /** Where ingest kept the original bytes of a LOADED upload: `{routeLong}raw/{date}/{uploadId}.{ext}` */
  rawKey?: string | null;
  /** Where a REJECTED file was moved, as sent: `{routeLong}rejected/{uploadId}.{ext}` */
  rejectedKey?: string | null;
  requestedAt: string;
  receivedAt: string | null;
  processedAt: string | null;
}

export interface DatasetUploadLink {
  uploadId: string;
  url: string;
  fields: Record<string, string>;
  expiresAt: string;
  maxBytes: number;
}

export interface DatasetUploadRequest {
  fileName: string;
  sourceTimezone?: string | null;
}

export type DatasetUploadStage = "requesting" | "uploading" | "processing";

export interface RunDatasetUploadOptions extends DatasetUploadRequest {
  onStage?: (stage: DatasetUploadStage) => void;
  /** The issued upload link, as soon as it exists; lets a caller label a "stop waiting" placeholder */
  onLinkIssued?: (link: DatasetUploadLink) => void;
  /** Bytes sent so far, out of the file's size; only during the "uploading" stage */
  onUploadProgress?: (sent: number, total: number) => void;
  /** Give up waiting (the upload itself continues server-side) after this long; default 2 minutes */
  waitTimeoutMs?: number;
  signal?: AbortSignal;
}

export interface RunDatasetUploadResult {
  upload: DatasetUploadRow;
  /** True when the status was still not final at waitTimeoutMs: the file was sent and ingest is still working */
  timedOut: boolean;
  /** Set when the file was sent but its status could not be read afterwards (the outcome is in the history) */
  readError?: string;
}

export const DATASET_UPLOAD_ALLOWED_EXTENSIONS = [".csv", ".csv.gz"];
// Mirrors DATASET_UPLOAD_MAX_BYTES in amplify/function/datasetIngestFunction/resource.ts. Checked here before a
// link is requested, so a file that can never be accepted leaves no REQUESTED row behind; the link's own
// maxBytes is the authority once it exists.
export const DATASET_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;

export const POLL_INTERVAL_MS = 3000;
const DEFAULT_WAIT_TIMEOUT_MS = 2 * 60 * 1000;

export const isFinalUploadStatus = (status: DatasetUploadStatus | null | undefined): boolean =>
  status === "LOADED" || status === "REJECTED" || status === "FAILED" || status === "EXPIRED";

export const UPLOAD_STATUS_LABELS: Record<DatasetUploadStatus, string> = {
  REQUESTED: "Waiting for file",
  RECEIVED: "Processing",
  LOADED: "Loaded",
  REJECTED: "Rejected",
  FAILED: "Failed",
  EXPIRED: "Not sent",
};

/** Extension and size are the only checks worth doing before asking for a link; ingest checks the content. */
export const datasetUploadFileError = (file: File | null | undefined, maxBytes: number = DATASET_UPLOAD_MAX_BYTES): string | null => {
  if (!file) return "Choose a file to upload.";
  const lower = file.name.toLowerCase();
  if (!DATASET_UPLOAD_ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
    return `Only ${DATASET_UPLOAD_ALLOWED_EXTENSIONS.join(" and ")} files can be uploaded.`;
  }
  if (file.size < 1) return "The file is empty.";
  if (file.size > maxBytes) return `The file is larger than the ${formatBytes(maxBytes)} limit.`;
  return null;
};

export const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * The zone name erodeapi accepts for the upload modal's timezone controls: an IANA name as is, or a fixed
 * offset as "UTC+5:30" (luxon's specifier; a fractional hour must be written as minutes).
 */
export const uploadTimezoneName = (
  timezoneType: "named" | "offset",
  namedZone: string | null | undefined,
  utcOffsetHours: number | null | undefined
): string | null => {
  if (timezoneType === "named") return namedZone || null;
  const hours = Number(utcOffsetHours) || 0;
  if (hours === 0) return "UTC";
  const sign = hours < 0 ? "-" : "+";
  const whole = Math.floor(Math.abs(hours));
  const minutes = Math.round((Math.abs(hours) - whole) * 60);
  return `UTC${sign}${whole}${minutes ? `:${String(minutes).padStart(2, "0")}` : ""}`;
};

/** The message a failed request carries in erodeapi's { statusCode, error } envelope, if it has one */
export const errorMessageOf = (error: any, fallback: string): string => {
  const body = error?.response?.body;
  if (typeof body === "string") {
    try {
      const parsed = JSON.parse(body);
      if (parsed?.error) return parsed.error;
    } catch {
      // not JSON: fall through to the generic message
    }
  }
  return error?.message || fallback;
};

export const idTokenOrThrow = async (): Promise<string> => {
  const session = await fetchAuthSession();
  const idToken = session?.tokens?.idToken?.toString();
  if (!idToken) throw new Error("You need to be signed in to upload data.");
  return idToken;
};

const datasetPath = (datasetId: string, suffix: string) => `/api/datasets/${encodeURIComponent(datasetId)}/uploads${suffix}`;

/** Step 1: an upload link plus a REQUESTED row, issued by erodeapi after its access check. */
export const requestDatasetUpload = async (datasetId: string, request: DatasetUploadRequest): Promise<DatasetUploadLink> => {
  const idToken = await idTokenOrThrow();
  const body: Record<string, string> = { fileName: request.fileName, source: "WEB" };
  if (request.sourceTimezone) body.sourceTimezone = request.sourceTimezone;
  try {
    const operation = post({
      apiName: "erodeapi",
      path: datasetPath(datasetId, ""),
      // erodeapi authorizes with the Cognito token itself (tokenCheck), not with IAM signing
      options: { headers: { Authorization: `Bearer ${idToken}` }, body, retryStrategy: { strategy: "no-retry" } },
    });
    const response = await operation.response;
    const payload = (await response.body.json()) as unknown as { body?: DatasetUploadLink };
    if (!payload?.body?.url || !payload.body.uploadId) throw new Error("Unexpected response when requesting the upload link.");
    return payload.body;
  } catch (error) {
    throw new Error(errorMessageOf(error, "Could not request an upload link."));
  }
};

/**
 * Step 2: the file goes straight to S3 with the presigned POST — every signed field first, the file last, as
 * S3 requires. XMLHttpRequest rather than fetch because only it reports upload progress.
 */
export const sendDatasetUploadFile = (
  link: DatasetUploadLink,
  file: File,
  onProgress?: (sent: number, total: number) => void,
  signal?: AbortSignal
): Promise<void> =>
  new Promise((resolve, reject) => {
    const form = new FormData();
    Object.entries(link.fields).forEach(([name, value]) => form.append(name, value));
    form.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", link.url);
    xhr.upload.onprogress = (event) => {
      if (onProgress && event.lengthComputable) onProgress(event.loaded, event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      // S3 answers a refused POST with an XML document; its Message says why (policy, size, expiry)
      const message = /<Message>([^<]*)<\/Message>/.exec(xhr.responseText || "")?.[1];
      reject(new Error(message ? `The storage service refused the file: ${message}` : `The file upload failed (HTTP ${xhr.status}).`));
    };
    xhr.onerror = () => reject(new Error("The file upload failed: the storage service could not be reached."));
    xhr.onabort = () => reject(new Error("Upload cancelled."));
    if (signal) {
      if (signal.aborted) {
        reject(new Error("Upload cancelled."));
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(form);
  });

/** Step 3: one upload's row, with EXPIRED derived by the API for a link that lapsed unused. */
export const fetchDatasetUpload = async (datasetId: string, uploadId: string): Promise<DatasetUploadRow> => {
  const idToken = await idTokenOrThrow();
  try {
    const operation = get({
      apiName: "erodeapi",
      path: datasetPath(datasetId, `/${encodeURIComponent(uploadId)}`),
      options: { headers: { Authorization: `Bearer ${idToken}` }, retryStrategy: { strategy: "no-retry" } },
    });
    const response = await operation.response;
    const payload = (await response.body.json()) as unknown as { body?: { upload?: DatasetUploadRow } };
    if (!payload?.body?.upload) throw new Error("Unexpected response when reading the upload's status.");
    return payload.body.upload;
  } catch (error) {
    throw new Error(errorMessageOf(error, "Could not read the upload's status."));
  }
};

export interface DatasetUploadPage {
  uploads: DatasetUploadRow[];
  nextToken: string | null;
}

/** The dataset's upload history, newest first. `status` filters on the status the API reports (EXPIRED included). */
export const listDatasetUploads = async (
  datasetId: string,
  options: { limit?: number; nextToken?: string | null; status?: DatasetUploadStatus | null } = {}
): Promise<DatasetUploadPage> => {
  const idToken = await idTokenOrThrow();
  const queryParams: Record<string, string> = {};
  if (options.limit) queryParams.limit = String(options.limit);
  if (options.status) queryParams.status = options.status;
  if (options.nextToken) queryParams.nextToken = options.nextToken;
  try {
    const operation = get({
      apiName: "erodeapi",
      path: datasetPath(datasetId, ""),
      options: { headers: { Authorization: `Bearer ${idToken}` }, queryParams, retryStrategy: { strategy: "no-retry" } },
    });
    const response = await operation.response;
    const payload = (await response.body.json()) as unknown as { body?: DatasetUploadPage };
    if (!payload?.body?.uploads) throw new Error("Unexpected response when listing uploads.");
    return { uploads: payload.body.uploads, nextToken: payload.body.nextToken || null };
  } catch (error) {
    throw new Error(errorMessageOf(error, "Could not load the upload history."));
  }
};

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error("Upload cancelled."));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error("Upload cancelled."));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });

/** What the modal shows for an upload S3 accepted but whose row could not be read back: sent, outcome pending */
export const sentUploadPlaceholder = (link: DatasetUploadLink, file: File): DatasetUploadRow => ({
  id: link.uploadId,
  datasetId: "",
  status: "RECEIVED",
  source: "WEB",
  fileName: file.name,
  uploadedBy: null,
  uploadedByEmail: null,
  sourceTimezone: null,
  sizeBytes: file.size,
  rowCount: null,
  duplicateTimestamps: null,
  timeRange: null,
  columns: null,
  validationErrors: null,
  warnings: null,
  failureReason: null,
  recordId: null,
  requestedAt: new Date().toISOString(),
  receivedAt: null,
  processedAt: null,
});

/**
 * Poll until ingest has finished with the upload, or the wait runs out. A transient read error does not end
 * the wait: the upload is being processed regardless of whether one status read failed.
 */
export const waitForDatasetUpload = async (
  datasetId: string,
  uploadId: string,
  options: { timeoutMs?: number; intervalMs?: number; signal?: AbortSignal } = {}
): Promise<RunDatasetUploadResult> => {
  const timeoutMs = options.timeoutMs ?? DEFAULT_WAIT_TIMEOUT_MS;
  const intervalMs = options.intervalMs ?? POLL_INTERVAL_MS;
  const deadline = Date.now() + timeoutMs;
  let last: DatasetUploadRow | null = null;
  let failedReads = 0;
  for (;;) {
    if (options.signal?.aborted) throw new Error("Upload cancelled.");
    try {
      last = await fetchDatasetUpload(datasetId, uploadId);
      failedReads = 0;
      if (isFinalUploadStatus(last.status)) return { upload: last, timedOut: false };
    } catch (error) {
      failedReads += 1;
      if (failedReads >= 3) throw error;
    }
    if (Date.now() >= deadline) {
      if (!last) throw new Error("Could not read the upload's status.");
      return { upload: last, timedOut: true };
    }
    await sleep(Math.min(intervalMs, Math.max(0, deadline - Date.now())), options.signal);
  }
};

/** The whole flow: link → file → status. Throws only when the file never reached S3 or a read kept failing. */
export const runDatasetUpload = async (datasetId: string, file: File, options: RunDatasetUploadOptions): Promise<RunDatasetUploadResult> => {
  const early = datasetUploadFileError(file);
  if (early) throw new Error(early);

  options.onStage?.("requesting");
  const link = await requestDatasetUpload(datasetId, {
    fileName: options.fileName || file.name,
    sourceTimezone: options.sourceTimezone,
  });
  options.onLinkIssued?.(link);
  const tooBig = datasetUploadFileError(file, link.maxBytes);
  if (tooBig) throw new Error(tooBig);

  options.onStage?.("uploading");
  await sendDatasetUploadFile(link, file, options.onUploadProgress, options.signal);

  options.onStage?.("processing");
  try {
    return await waitForDatasetUpload(datasetId, link.uploadId, { timeoutMs: options.waitTimeoutMs, signal: options.signal });
  } catch (error: any) {
    // The file is in S3 and ingest will load it whether or not we can read the row: never report it as failed,
    // or the obvious next click uploads the same rows again. A cancel is the caller's own decision, rethrown.
    if (options.signal?.aborted) throw error;
    return { upload: sentUploadPlaceholder(link, file), timedOut: true, readError: error?.message };
  }
};

/** The stored copy of what the sender sent: raw/ for a loaded file, rejected/ for a refused one. */
export const uploadOriginalKey = (upload: Pick<DatasetUploadRow, "rawKey" | "rejectedKey">): string | null =>
  upload.rawKey || upload.rejectedKey || null;

/** The stored path without the dataset's folder prefix: `raw/2026-09-22/{uploadId}.csv.gz`. */
export const shortStorageKey = (key: string | null | undefined): string => {
  if (!key) return "";
  const match = /\/datasets\/[^/]+\/(.*)$/.exec(key);
  return match ? match[1] : key;
};

/** Saves the original file of an upload exactly as stored (a .csv.gz stays compressed) under its own name. */
export const downloadUploadOriginal = async (upload: DatasetUploadRow): Promise<void> => {
  const key = uploadOriginalKey(upload);
  if (!key) throw new Error("This upload has no stored original.");
  try {
    const result = await downloadData({ path: key }).result;
    const blob = await result.body.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = upload.fileName || key.split("/").pop() || "upload.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    throw new Error(errorMessageOf(error, "Could not download the original file."));
  }
};

export interface UploadDetail {
  label: string;
  value: string;
}

/** The facts the Uploads tab lists for an expanded row, in reading order; a fact the upload lacks is left out. */
export const uploadDetailRows = (upload: DatasetUploadRow): UploadDetail[] => {
  const rows: UploadDetail[] = [];
  const add = (label: string, value: string | null | undefined) => {
    if (value) rows.push({ label, value });
  };
  add("Sent from", upload.source === "WEB" ? "The web app" : upload.source === "API" ? "The API" : upload.source === "MODEL" ? "A model run" : null);
  add("Uploaded by", upload.uploadedByEmail);
  add("File size", upload.sizeBytes ? formatBytes(upload.sizeBytes) : null);
  add("Rows", upload.rowCount === null || upload.rowCount === undefined ? null : upload.rowCount.toLocaleString());
  if (upload.timeRange?.startDate && upload.timeRange?.endDate) {
    add("Time range", `${formatUtc(upload.timeRange.startDate)} to ${formatUtc(upload.timeRange.endDate)}`);
  }
  add("Columns", upload.columns?.length ? upload.columns.join(", ") : null);
  // The dataset's mapping as applied to this file. A snapshot, so editing the mapping later doesn't change it.
  add("Timestamp read from", upload.columnSources?.timestamp);
  const renamed = Object.entries(upload.columnSources?.columns || {}).filter(([stored, sent]) => stored !== sent);
  add("Renamed on ingest", renamed.length ? renamed.map(([stored, sent]) => `${sent} → ${stored}`).join(", ") : null);
  add("Repeated timestamps", upload.duplicateTimestamps ? upload.duplicateTimestamps.toLocaleString() : null);
  add("Timezone read as", typeof upload.sourceTimezone === "string" ? upload.sourceTimezone : null);
  add("Link requested", formatUtc(upload.requestedAt));
  add("File received", formatUtc(upload.receivedAt));
  add("Processed", formatUtc(upload.processedAt));
  return rows;
};

/** A one-line summary of a finished upload for the modal and the history list. */
export const describeDatasetUpload = (upload: DatasetUploadRow): string => {
  switch (upload.status) {
    case "LOADED": {
      const rows = upload.rowCount === null || upload.rowCount === undefined ? "Loaded" : `${upload.rowCount.toLocaleString()} rows loaded`;
      const range =
        upload.timeRange?.startDate && upload.timeRange?.endDate
          ? `, ${formatUtc(upload.timeRange.startDate)} to ${formatUtc(upload.timeRange.endDate)}`
          : "";
      return `${rows}${range}`;
    }
    case "REJECTED":
      return upload.validationErrors?.length ? upload.validationErrors.join(" ") : "The file was rejected.";
    case "FAILED":
      return upload.failureReason ? `Processing failed: ${upload.failureReason}` : "Processing failed.";
    case "EXPIRED":
      return "The upload link expired before a file was sent.";
    case "RECEIVED":
      return "The file arrived and is being processed.";
    default:
      return "Waiting for the file.";
  }
};

/**
 * Properties for the DATASET_UPLOAD_COMPLETED analytics event: the outcome of one web upload. `status` is the
 * upload's final status, PROCESSING when the wait timed out with ingest still at work, and ERROR when the file
 * never got a result (the link or the send failed). No result at all means the error path.
 */
export const datasetUploadEventProperties = (
  dataset: { id: string; datasetType?: string | null; parameterType?: string | null; organizationId?: string | null },
  file: File | null | undefined,
  { result, fromCreateFlow }: { result?: RunDatasetUploadResult; fromCreateFlow: boolean }
): Record<string, unknown> => {
  const upload = result?.upload;
  const name = file?.name?.toLowerCase() || "";
  const extension = DATASET_UPLOAD_ALLOWED_EXTENSIONS.find((ext) => name.endsWith(ext)) || null;
  return {
    dataset_id: dataset.id,
    dataset_type: dataset.datasetType || null,
    parameter_type: dataset.parameterType || null,
    owner_type: dataset.organizationId ? "organization" : "personal",
    source: "WEB",
    status: !result ? "ERROR" : result.timedOut ? "PROCESSING" : upload?.status || "UNKNOWN",
    row_count: upload?.rowCount ?? null,
    size_bytes: file?.size ?? null,
    file_extension: extension,
    warning_count: upload?.warnings?.length || 0,
    rejection_count: upload?.validationErrors?.length || 0,
    from_create_flow: fromCreateFlow,
  };
};

/** "2026-09-22 14:05 UTC": the pipeline's times are UTC and the chart's axis is too */
export const formatUtc = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.toISOString().slice(0, 16).replace("T", " ")} UTC`;
};

/** An upload's data range as a short chip, e.g. "Sep 1 – Sep 6, 2026" (UTC); null when the file carried no range. */
export const formatUploadRange = (upload: DatasetUploadRow): string | null => {
  const { startDate, endDate } = upload.timeRange || {};
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const day = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  // A range across New Year names both years, e.g. "Dec 28, 2026 – Jan 3, 2027"
  const startYear = start.getUTCFullYear() !== end.getUTCFullYear() ? `, ${start.getUTCFullYear()}` : "";
  return `${day(start)}${startYear} – ${day(end)}, ${end.getUTCFullYear()}`;
};

/** When an upload landed, as a table cell, e.g. "Oct 13, 3:24 PM" (UTC, like the rest of the log). */
export const formatUploadDay = (iso?: string | null): string => {
  if (!iso) return "—";
  if (Number.isNaN(new Date(iso).getTime())) return "—";
  return (
    new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "UTC" }) + " UTC"
  );
};
